import { Clear } from "@procore/core-icons";
import { Box, Button, spacing } from "@procore/core-react";
import { type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import ExpandIcon from "media/icons/expand.svg?react";
import MinimizeIcon from "media/icons/minimize.svg?react";

import { useWindowMinimize, type WindowMinimize } from "./windowMinimizeContext";

export interface WindowChromeControlsProps {
    onClose?: () => void;
    /**
     * Explicit minimize control. Falls back to `WindowMinimizeContext` when omitted.
     * Pass `null` to suppress minimize entirely, even inside a managed window.
     */
    minimize?: WindowMinimize | null;
}

/**
 * `@procore/core-react`'s Button paints a focus ring on `:focus` (not `:focus-visible`),
 * so mouse clicks leave a lingering ring. Blocking default focus on mousedown suppresses
 * it while keeping Tab/Enter/Space focus intact.
 */
function suppressMouseFocus(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
}

/**
 * Close + minimize button group used by `WindowChrome` and exported for consumers
 * using the `header` escape hatch. Renders nothing when both controls are absent.
 */
export function WindowChromeControls({ onClose, minimize }: WindowChromeControlsProps) {
    const { t } = useTranslation();
    const contextMinimize = useWindowMinimize();
    const effective = minimize === null ? null : (minimize ?? contextMinimize);
    const isMinimized = effective?.minimized ?? false;

    if (!effective && !onClose) {
        return null;
    }

    return (
        <Box style={{ display: "flex", flexShrink: 0, gap: spacing.xs }}>
            {effective && (
                <Button
                    icon={isMinimized ? <ExpandIcon /> : <MinimizeIcon />}
                    variant="tertiary"
                    size="sm"
                    onMouseDown={suppressMouseFocus}
                    onClick={effective.onToggle}
                    aria-label={isMinimized ? t("expand") : t("minimize")}
                    data-testid="window-minimize-toggle"
                />
            )}
            {onClose && (
                <Button
                    icon={<Clear />}
                    variant="tertiary"
                    size="sm"
                    onMouseDown={suppressMouseFocus}
                    onClick={onClose}
                    aria-label={t("close")}
                />
            )}
        </Box>
    );
}
